import { adSlug } from "@/lib/seo";
import { UserAvatar } from "@/components/user-avatar";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, BadgeCheck, BarChart3, Bell, Camera, Crown, Heart, ImagePlus, LogOut, MessageCircle, Pencil, Plus, Sparkles, RefreshCw, Trash2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdCard } from "@/components/ad-card";
import { ads, formatPrice } from "@/lib/data";
import { supabase } from "@/integrations/supabase/client";
import { fetchMyListings, PLACEHOLDER_IMAGE, setAutoRenew, statusLabels, uploadAvatar } from "@/lib/listings";
import { azError } from "@/lib/az-errors";
import { RecoveryWordCard } from "@/components/recovery-word-card";

export const Route = createFileRoute("/_authenticated/profil")({
  head: () => ({
    meta: [
      { title: "Profilim — Albazar.az" },
      { name: "description", content: "Profil məlumatlarınızı redaktə edin, elanlarınızı və seçilmişləri idarə edin." },
      { property: "og:title", content: "Profilim — Albazar.az" },
      { property: "og:description", content: "Elanlarınızı idarə edin." },
    
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: Profile,
});

function Profile() {
  const { user } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const favs = ads.slice(8, 12);

  const { data: myAds = [], isLoading: adsLoading } = useQuery({
    queryKey: ["my-listings", user.id],
    queryFn: () => fetchMyListings(user.id),
  });

  const refreshAds = () => queryClient.invalidateQueries({ queryKey: ["my-listings", user.id] });

  const setStatus = async (id: string, status: "active" | "draft") => {
    const { error } = await supabase.from("listings").update({ status }).eq("id", id);
    if (error) {
      toast.error(azError(error));
      return;
    }
    toast.success(status === "active" ? "Elan dərc olundu" : "Elan yayımdan çıxarıldı");
    await refreshAds();
  };

  const remove = async (id: string) => {
    if (!window.confirm("Elanı silmək istədiyinizə əminsiniz?")) return;
    const { error } = await supabase.from("listings").delete().eq("id", id);
    if (error) {
      toast.error(azError(error));
      return;
    }
    toast.success("Elan silindi");
    await refreshAds();
  };

  const bump = async (id: string) => {
    const { error } = await supabase
      .from("listings")
      .update({ updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      toast.error(azError(error));
      return;
    }
    toast.success("Elan yeniləndi");
    await refreshAds();
  };

  const { data: profile } = useQuery({
    queryKey: ["profile", user.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, phone, created_at, avatar_url, banner_url, balance")
        .eq("id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: store } = useQuery({
    queryKey: ["my-store", user.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stores")
        .select("id, name, verified, status")
        .eq("owner_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const [form, setForm] = useState({ full_name: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);

  const onAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadAvatar(user.id, file);
      const { error } = await supabase
        .from("profiles")
        .upsert({ id: user.id, avatar_url: url });
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
      toast.success("Profil şəkli yeniləndi");
    } catch (err) {
      toast.error(azError(err));
    } finally {
      setUploading(false);
    }
  };

  const onBanner = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBannerUploading(true);
    try {
      const url = await uploadAvatar(user.id, file);
      const { error } = await supabase.from("profiles").upsert({ id: user.id, banner_url: url });
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
      toast.success("Banner yeniləndi");
    } catch (err) {
      toast.error(azError(err));
    } finally {
      setBannerUploading(false);
    }
  };

  useEffect(() => {
    if (profile) setForm({ full_name: profile.full_name ?? "", phone: profile.phone ?? "" });
  }, [profile]);

  const displayName = profile?.full_name?.trim() || user.email || "İstifadəçi";
  const memberSince = profile?.created_at ? new Date(profile.created_at).getFullYear() : null;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .upsert({ id: user.id, full_name: form.full_name, phone: form.phone });
    setSaving(false);
    if (error) {
      toast.error(azError(error));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
    toast.success("Profil yeniləndi");
  };

  const signOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/giris", replace: true });
  };

  return (
    <div className="container-x py-10">
      <div className="relative mb-4 overflow-hidden rounded-3xl border border-border bg-muted shadow-card">
        {profile?.banner_url ? (
          <img
            src={profile.banner_url}
            alt={`${displayName} profil banneri`}
            className="h-36 w-full object-cover sm:h-48"
            loading="lazy"
          />
        ) : (
          <div className="h-36 w-full bg-gradient-to-r from-primary/25 via-info/20 to-accent/25 sm:h-48" />
        )}
        <label className="absolute bottom-3 right-3 inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-card/90 px-3 py-1.5 text-xs font-semibold shadow-md backdrop-blur">
          <ImagePlus className="size-3.5" />
          {bannerUploading ? "Yüklənir..." : "Banner dəyiş"}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={bannerUploading}
            onChange={(e) => void onBanner(e)}
          />
        </label>
      </div>

      <div className="flex flex-col gap-5 rounded-3xl border border-border bg-card p-6 shadow-card sm:flex-row sm:items-center">
        <div className="relative size-20 shrink-0">
          <UserAvatar src={profile?.avatar_url} name={displayName} className="size-20" />
          <label className="absolute -bottom-1 -right-1 grid size-8 cursor-pointer place-items-center rounded-full bg-primary text-primary-foreground shadow-md">
            <Camera className="size-4" />
            <span className="sr-only">
              {uploading ? "Profil şəkli yüklənir" : "Profil şəkli yüklə və ya dəyiş"}
            </span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => void onAvatar(e)}
            />
          </label>
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold">{displayName}</h1>
          <label className="mt-1 inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-info hover:underline">
            <Camera className="size-3.5" />
            {uploading ? "Yüklənir..." : "Profil şəklini dəyiş"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => void onAvatar(e)}
            />
          </label>
          <p className="text-sm text-muted-foreground">
            {[profile?.phone, user.email].filter(Boolean).join(" • ")}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {memberSince && (
              <Badge variant="secondary" className="rounded-full">{memberSince}-dan üzv</Badge>
            )}
            {store?.verified && store.status === "active" ? (
              <Badge className="rounded-full bg-info text-background">
                <BadgeCheck className="mr-1 size-3.5" /> Təsdiqlənmiş mağaza
              </Badge>
            ) : store ? (
              <Badge variant="outline" className="rounded-full">Mağaza (gözləyir)</Badge>
            ) : null}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/secilmisler"><Button variant="outline" className="rounded-full" aria-label="Seçilmişlər"><Heart className="size-4" /></Button></Link>
          <Link to="/mesajlar" search={{ user: "" }}><Button variant="outline" className="rounded-full" aria-label="Mesajlar"><MessageCircle className="size-4" /></Button></Link>
          <Link to="/statistika">
            <Button variant="outline" className="rounded-full" aria-label="Statistika">
              <BarChart3 className="size-4" />
            </Button>
          </Link>
          <Link to="/bildirisler"><Button variant="outline" className="rounded-full" aria-label="Bildirişlər"><Bell className="size-4" /></Button></Link>
          <Button variant="outline" onClick={signOut} className="rounded-full">
            <LogOut className="size-4" /> Çıxış
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-3xl border border-border bg-card p-5 shadow-card">
          <p className="text-sm text-muted-foreground">Balansınız</p>
          <p className="mt-1 text-2xl font-black">
            {Number(profile?.balance ?? 0).toFixed(2)} AZN
          </p>
          <Link to="/balans" className="mt-2 inline-block text-sm font-bold text-primary underline">
            Balans artır
          </Link>
        </div>
        <div className="rounded-3xl border border-border bg-card p-5 shadow-card">
          <p className="text-sm text-muted-foreground">Elanlarım</p>
          <p className="mt-1 text-2xl font-black">{myAds.length}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Aktiv: {myAds.filter((a) => a.status === "active").length}
          </p>
        </div>
        <div className="rounded-3xl border border-border bg-card p-5 shadow-card">
          <p className="text-sm text-muted-foreground">Ümumi baxış</p>
          <p className="mt-1 text-2xl font-black">
            {myAds.reduce((s, a) => s + Number(a.views ?? 0), 0)}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">Bütün elanlar üzrə</p>
        </div>
      </div>

      <Tabs defaultValue="ads" className="mt-8">
        <TabsList className="rounded-full">
          <TabsTrigger value="ads" className="rounded-full">Elanlarım</TabsTrigger>
          <TabsTrigger value="fav" className="rounded-full">Seçilmişlər</TabsTrigger>
          <TabsTrigger value="edit" className="rounded-full">Profili redaktə et</TabsTrigger>
        </TabsList>

        <TabsContent value="ads" className="mt-6">
          <div className="mb-4 flex justify-end">
            <Link to="/yeni-elan">
              <Button className="rounded-full"><Plus className="size-4" /> Yeni elan</Button>
            </Link>
          </div>
          {adsLoading ? (
            <p className="text-sm text-muted-foreground">Yüklənir...</p>
          ) : myAds.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border p-10 text-center">
              <p className="font-semibold">Hələ elanınız yoxdur</p>
              <p className="mt-1 text-sm text-muted-foreground">İlk elanınızı bir neçə dəqiqəyə yerləşdirin.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {myAds.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-card p-3 shadow-card">
                  <img
                    src={a.images[0] ?? PLACEHOLDER_IMAGE}
                    alt={a.title}
                    loading="lazy"
                    className="size-20 rounded-xl object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <Link to="/elan/$id" params={{ id: adSlug({ id: a.id, title: a.title, city: a.city }) }} className="truncate font-semibold hover:text-primary">
                      {a.title}
                    </Link>
                    <p className="text-sm text-primary">{formatPrice(Number(a.price))}</p>
                    <p className="text-xs text-muted-foreground">{a.views} baxış • {a.city}</p>
                  </div>
                  <Badge variant={a.status === "active" ? "secondary" : "outline"} className="rounded-full">
                    {statusLabels[a.status] ?? a.status}
                  </Badge>
                  <div className="flex items-center gap-1">
                    <Link to="/elan-redakte/$id" params={{ id: a.id }}>
                      <Button size="sm" variant="outline" className="rounded-full">
                        <Pencil className="size-4" /> Redaktə et
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full text-info"
                      onClick={() => void bump(a.id)}
                    >
                      <Zap className="size-4" /> İrəli çək
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className={`rounded-full ${a.auto_renew ? "text-success" : ""}`}
                      onClick={async () => {
                        try {
                          await setAutoRenew(a.id, !a.auto_renew);
                          toast.success(
                            a.auto_renew
                              ? "Avtomatik irəli çəkmə söndürüldü"
                              : "Avtomatik irəli çəkmə aktivdir (gündə 1 dəfə)",
                          );
                          queryClient.invalidateQueries({ queryKey: ["my-listings"] });
                        } catch (e) {
                          toast.error(azError(e));
                        }
                      }}
                    >
                      <RefreshCw className="size-4" /> {a.auto_renew ? "Avto: açıq" : "Avto: bağlı"}
                    </Button>
                    <Link to="/elan-reklam/$id" params={{ id: a.id }} search={{ plan: "vip" as const, days: 7 }}>
                      <Button size="sm" variant="outline" className="rounded-full text-vip">
                        <Crown className="size-4" /> VIP et
                      </Button>
                    </Link>
                    <Link to="/elan-reklam/$id" params={{ id: a.id }} search={{ plan: "premium" as const, days: 7 }}>
                      <Button size="sm" variant="outline" className="rounded-full text-premium">
                        <Sparkles className="size-4" /> Premium et
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => void setStatus(a.id, a.status === "active" ? "draft" : "active")}
                    >
                      <Archive className="size-4" />
                      {a.status === "active" ? "Arxivlə" : "Dərc et"}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="rounded-full text-destructive"
                      aria-label="Sil"
                      onClick={() => void remove(a.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="fav" className="mt-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {favs.map((a, i) => <AdCard key={a.id} ad={a} index={i} />)}
          </div>
        </TabsContent>

        <TabsContent value="edit" className="mt-6">
          <form onSubmit={save} className="max-w-lg space-y-4 rounded-3xl border border-border bg-card p-6 shadow-card">
            <div>
              <Label htmlFor="n">Ad, Soyad</Label>
              <Input id="n" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} className="mt-2 h-11 rounded-2xl" />
            </div>
            <div>
              <Label htmlFor="p">Telefon</Label>
              <Input id="p" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="mt-2 h-11 rounded-2xl" />
            </div>
            <div>
              <Label htmlFor="e">E-poçt</Label>
              <Input id="e" value={user.email ?? ""} disabled className="mt-2 h-11 rounded-2xl" />
            </div>
            <Button type="submit" disabled={saving} className="h-11 rounded-2xl">
              {saving ? "Yadda saxlanılır..." : "Yadda saxla"}
            </Button>
          </form>
          <RecoveryWordCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
